// ---------------------------------------------------------------------------------------------
// HER SIGNUP PAGE — **HER ARTWORK IS THE PAGE.**
//
// The reasoning, the measured coordinates and every law this renders live in `src/signupPage.js`,
// which imports nothing but two laws that import nothing themselves — so a `.mjs` gate LOADS and
// RUNS all of it with no browser. This file is the rendering and the wiring and holds no law of its
// own, which is what keeps the two from drifting (R28/R29).
//
// >>> WHAT IS REBUILT AS TYPE HERE: NOTHING. <<< "Create your room.", "Continue with Google", the
// "or" rule, "Continue" and "Your journey is private and secure." are all painted INTO her
// composition. What this adds is two transparent targets over her two painted buttons and the two
// LIVE fields in the one band she left empty. The mockup-matching law read literally.
import { safeSessionStorage } from "../lib/safe-storage";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import {
  signupControls, signupFieldBand, controlCenter,
  signupSubmitState, signupMaySubmit, signupHint, signupIntentLine,
  SIGNUP_ART_BREAKPOINT, NAME_REQUIRED_CODE,
} from "../signupPage.js";
import { emailFieldState } from "../emailAddress.js";
import { signInEmailSeed, readStashedPrefill } from "../lib/funnel-prefill";
import desktopArt from "../assets/signup-final.webp";
import phoneArt from "../assets/signup-final-v.webp";
import type { MemberSession } from "../lib/session-restore";

type Control = { id: string; kind: string; label: string; left: number; top: number; width: number; height: number };

export function SignupPage({ intent = "join", onMemberChange, leadEmail, notice, onOpenDoc }: {
  /** `join` or `login`. Her artwork paints ONE card, so the DOOR is the same and the SENTENCE differs. */
  intent?: string;
  onMemberChange: (member: MemberSession) => void;
  /** L10: the address her funnel already has, when this page was reached by her bridge. */
  leadEmail?: string;
  notice?: string | null;
  /** L3 (her K4-Q1 YES, 2026-10-20): the agreement line's two links open the same popups as the room-creation step. */
  onOpenDoc?: (doc: "terms" | "rules") => void;
}) {
  // The MAP has to match the ART the browser chose, and only the browser knows which that is. Read
  // once and on change rather than guessed from a user agent, because a phone in landscape and a
  // small desktop window are the same question. HOME-1's own pattern, one surface over.
  const [wide, setWide] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth > SIGNUP_ART_BREAKPOINT);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${SIGNUP_ART_BREAKPOINT + 1}px)`);
    const read = () => setWide(mq.matches);
    read();
    mq.addEventListener("change", read);
    return () => mq.removeEventListener("change", read);
  }, []);

  // >>> THE HOUSE STANDS DOWN FOR HER ARTWORK HERE EXACTLY AS IT DOES ON HER HOME PAGE. <<< The
  // class rides the BODY because the sidebar mounts outside this page's tree, and it is the SAME
  // class rather than a second one: *her artwork is the page and the house's own chrome stands down*
  // is one fact about a surface, and two spellings of it is two answers (R28/R29). Mounted means
  // collapsed, unmounted means restored, by construction.
  //
  // **AND `art-draws-menu` IS NOT ADDED**, which is the home page's own law doing its job here for
  // free: her signup composition paints no menu, so the house's collapse affordance stays available
  // and **a member who lands here and changes her mind is never stranded** — which is her own clause.
  useEffect(() => {
    document.body.classList.add("is-stranger-home");
    document.body.classList.add("is-signup-page");
    return () => {
      document.body.classList.remove("is-stranger-home");
      document.body.classList.remove("is-signup-page");
    };
  }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => signInEmailSeed("", { leadEmail: leadEmail || readStashedPrefill(safeSessionStorage())?.email }));
  const [status, setStatus] = useState("");
  // >>> A STRANGER WHO HAS JUST ARRIVED IS NOT TOLD WHAT SHE HAS DONE WRONG. <<< The first render of
  // this page printed *"Add your name to continue."* under an empty form — the house scolding
  // somebody her advertising has just paid to bring here, before she has touched anything. The
  // "you have not typed that yet" hints are held until she really presses Continue; her suggester's
  // own "did you mean" still speaks the moment there is an address to question, because that one is
  // about something she DID type.
  const [attempted, setAttempted] = useState(false);
  const [googleWait, setGoogleWait] = useState(false);
  // D-5's ruling: the in-flight guard is a REF, because a `useState` guard is read from the render
  // that BOUND the handler — so two taps inside one frame both send, and it passes every presence
  // check while doing so. What the button WEARS is state, so the refusal is visible (R25).
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  // >>> **AUTH-BREACH-1 — THE SECOND STEP, WHICH ONLY EXISTS ONCE THE HOUSE KNOWS HER.** <<< Her
  // artwork paints ONE card, two fields and a Continue, and it stays exactly as she drew it. This
  // step opens over it only after the server has said the address already belongs to somebody.
  const [step, setStep] = useState<'identify' | 'password'>('identify');
  const [password, setPassword] = useState("");
  const [linkSent, setLinkSent] = useState("");


  // **L12 — THE LEAD'S ADDRESS ARRIVES AFTER THIS PAGE HAS ALREADY MOUNTED.** Measured at
  // coordinates on the modal this page replaces: the bridge raised it at 276ms and
  // `/api/funnel/prefill` resolved afterwards, so a `useState` seed alone — which reads its argument
  // ONCE — is the whole reason a stranger off her advertisement was asked to re-type the address she
  // had just given the landing page. `signInEmailSeed` owns the ordering: what is in the box always
  // wins, so this can never rewrite itself under her fingers.
  useEffect(() => {
    if (!leadEmail) return;
    setEmail((current) => signInEmailSeed(current, { leadEmail }));
  }, [leadEmail]);

  const controls = signupControls(wide) as Control[];
  const band = signupFieldBand(wide);
  // >>> **THE ADDRESS IS WHAT CONTINUE NEEDS. THE NAME BELONGS TO A BIRTH, AND ONLY THE SERVER
  // KNOWS THIS IS ONE (2026-09-04).** <<< The law's own block carries the member's words: this page
  // became the sign-in door on 2026-09-03 and went on demanding a name from members signing back in.
  const submitState = signupSubmitState({ email });
  const emailField = emailFieldState(email);

  async function submit(event?: FormEvent) {
    if (event) event.preventDefault();
    if (inFlight.current) return;
    setAttempted(true);
    if (!signupMaySubmit(submitState)) { setStatus(signupHint(submitState)); return; }
    inFlight.current = true;
    setBusy(true);
    setStatus("");
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email }),
      });
      const data = await response.json();
      // >>> **AUTH-BREACH-1 — THE ADDRESS IS KNOWN, SO IT NEEDS A SECRET.** <<< `6d73b77e` closed the
      // door where an address alone opened an account. The server answers `password-required` for
      // every address that already belongs to somebody, and THAT IS NOT AN ERROR TO THROW AT HER —
      // it is the next step. Her artwork paints two fields and a Continue; this opens the second
      // step BESIDE it rather than repainting her master, which is her standing mockup law.
      if (response.status === 403 && data?.code === "password-required") {
        setStep("password");
        setStatus("");
        return;
      }
      // >>> **A BIRTH ASKS FOR HER NAME, AND IT IS ASKED HERE BECAUSE HERE IS WHERE IT IS KNOWN
      // TO BE A BIRTH.** <<< A returning member never reaches this line: her address was refused
      // above with `password-required` and the panel opened. The sentence is the roster's own,
      // never a second spelling of the server's (R28/R29).
      if (response.status === 400 && data?.code === NAME_REQUIRED_CODE) {
        setStatus(signupHint("needs-name"));
        return;
      }
      if (!response.ok) throw new Error(data?.message || "That did not go through. Try once more.");
      onMemberChange(data.member);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "That did not go through. Try once more.");
    } finally {
      // A guard that never reopens is a dead control, not a safe one (D-5), so it is put back in a
      // `finally` — this is the one field on the page whose stuck state is a permanently dead
      // Continue with her name and address still sitting in it.
      inFlight.current = false;
      setBusy(false);
    }
  }

  // AUTH-BREACH-1 — she has a password and is using it.
  async function signInWithPassword(event?: FormEvent) {
    if (event) event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setStatus("");
    try {
      const response = await fetch("/api/auth/password", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "That did not go through. Try once more.");
      setPassword("");
      onMemberChange(data.member);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "That did not go through. Try once more.");
    } finally { inFlight.current = false; setBusy(false); }
  }

  // >>> **THE LINK IS THE WAY BACK IN FOR EVERY MEMBER WHO HAS NO PASSWORD YET**, which today is
  // most of them. It answers the same sentence whoever asks, so this page cannot be used to find
  // out who is a member.
  async function sendClaimLink() {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setStatus("");
    try {
      const response = await fetch("/api/auth/claim/start", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      setLinkSent(data?.message || "If that address has an account with us, a link is on its way.");
    } catch {
      setLinkSent("If that address has an account with us, a link is on its way.");
    } finally { inFlight.current = false; setBusy(false); }
  }
  function fire(kind: string) {
    if (kind === "google") { setGoogleWait(true); window.location.href = "/api/auth/google/start"; return; }
    if (kind === "continue") { void submit(); }
  }

  const hint = status || (attempted ? signupHint(submitState) : "");

  return (
    <section className="signupart" aria-label="Create your room">
      {/* Her artwork carries the words as PIXELS, which say nothing to a screen reader, so the
          page's heading and its one line of guidance live here and are the only text this component
          renders that is not a field. */}
      <h1 className="visually-hidden">Create your room</h1>
      <p className="visually-hidden">{signupIntentLine(intent)}</p>
      <picture>
        <source media={`(max-width: ${SIGNUP_ART_BREAKPOINT}px)`} srcSet={phoneArt} />
        <img className="signupart__img" src={desktopArt} alt="" fetchPriority="high" decoding="async" />
      </picture>

      {/* HER TWO PAINTED BUTTONS, AS TARGETS. Real `<button>`s so they are reachable by keyboard and
          announced by a screen reader, positioned from their CENTRE so the sheet's 44px floor grows
          them without sliding her painted element into a corner. */}
      <div className="signupart__controls">
        {controls.map((c) => {
          const at = controlCenter(c);
          return (
            <button
              key={c.id}
              type="button"
              data-signup-control={c.id}
              className={`signupart__control signupart__control--${c.kind}${c.kind === "google" && googleWait ? " is-generating" : ""}`}
              style={{ top: `${at ? at.y : c.top}%`, left: `${at ? at.x : c.left}%`, width: `${c.width}%`, height: `${c.height}%` }}
              aria-label={c.label}
              /* >>> THE CONTROL SHOWS THE REFUSAL (R25). <<< Her signup-address law is that the
                 submit really refuses on the verdict rather than only inside the handler -- a door
                 that looks live and answers nothing is worse than one that says no. Only a BLOCKING
                 address shuts it: an empty box is not a mistake she has made yet, and the sentence
                 below tells her which one it is. */
              disabled={c.kind === "continue" ? busy || emailField.blocking : googleWait}
              onClick={() => fire(c.kind)}
            />
          );
        })}
      </div>

      {/* HER RESERVED SPACE. The band she left empty between the "or" rule and Continue, and the two
          fields SHARE an edge rather than a gap for the reason written in the law: on a 393px phone
          her band is 91px and two fields at HI1's floor need 88. */}
      <form
        className="signupart__fields"
        style={{ top: `${band.top}%`, left: `${band.left}%`, width: `${band.width}%`, height: `${band.height}%` }}
        onSubmit={submit}
      >
        <input
          className="signupart__field"
          data-signup-field="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          aria-label="Your name"
          autoComplete="name"
        />
        {/* >>> `type="email"` IS THE ONE-LINE ABSENCE THAT LET FOURTEEN UNREACHABLE ADDRESSES ONTO
            THIS SITE. <<< Her signup-address round found `i@mmoney888!`, `@g.comtec` and
            `d@j@nique09` as live accounts, every one through this door, because the browser's own
            format check never ran. It leads the attribute list for the same reason it did there:
            this is the attribute a careless edit drops. */}
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          className="signupart__field"
          data-signup-field="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          aria-label="Your email"
          aria-invalid={emailField.state === "invalid" ? true : undefined}
          // The hint is attached to the field, not merely printed near it: a sentence that exists
          // only visually is one a member using a screen reader never hears.
          aria-describedby={emailField.hint ? "signin-email-hint" : undefined}
        />
        {/* The submit lives here so a member who presses Enter in either box is carried by her own
            Continue rather than by nothing at all. It paints nothing: her button is the one on the
            artwork, and this one is named so it is never an unlabelled control (R43). */}
        <button type="submit" className="visually-hidden" tabIndex={-1} aria-label="Continue" />
      </form>

      {/* L3 (her K4-Q1 YES, 2026-10-20): the agreement, BELOW her artwork and never on it, the same sentence the
          room-creation step carries. Drawn before the note so the note's rule can clear it. */}
      <p className="signupart__agree">
        By signing up, you agree to our{" "}
        <button type="button" className="consent-agree__link" onClick={() => onOpenDoc?.("terms")}>Terms</button>
        {" "}and{" "}
        <button type="button" className="consent-agree__link" onClick={() => onOpenDoc?.("rules")}>house rules</button>.
      </p>

      {/* One line, under her card, for whatever the page has to say back — an address her suggester
          questions, a box she has not filled in, or the server's own refusal. It is drawn ONLY when
          there is something to say (T9-2: no body, no box). */}
      {notice || hint || emailField.hint ? (
        <p
          className="signupart__note"
          id="signin-email-hint"
          role="status"
          /* >>> BELOW HER ARTWORK, NEVER OVER IT. <<< The first render put this one line at the foot
             of the field band — which is exactly where her painted Continue button begins, so the
             note covered it and **the button stopped answering a hit test at its own centre**. A
             surface that eats its own primary control is the dead tap the whole map exists to
             prevent, and it was invisible in a screenshot. Below the artwork there is room at every
             width and it can never cover a pixel she painted. */
        >
          {notice || hint || emailField.hint}
          {/* >>> HER SUGGESTION IS OFFERED, NEVER APPLIED. <<< Silently rewriting an address signs
              her up as somebody else and she finds out when nothing arrives — this round's own
              defect wearing a fix. She taps it, or she does not. */}
          {!notice && !hint && emailField.suggestion ? (
            <button
              type="button"
              className="signupart__suggest"
              onClick={() => setEmail(emailField.suggestion)}
            >
              {emailField.suggestion}
            </button>
          ) : null}
        </p>
      ) : null}

      {/* >>> **AUTH-BREACH-1 — THE SECOND STEP, OVER HER CARD AND NEVER INSTEAD OF IT.** <<< Her
          artwork is untouched: this panel only exists once the server has said the address already
          belongs to somebody, which is the moment a secret is required. */}
      {step === "password" ? (
        <div className="signin-second" role="dialog" aria-label="Sign in with your password">
          <div className="signin-second__card">
            <h2 className="signin-second__title">Welcome back</h2>
            {linkSent ? (
              <p className="signin-second__line">{linkSent}</p>
            ) : (
              <>
                <p className="signin-second__line">
                  Sign-in just got stronger. If you have set a password, type it below. If you have not,
                  send yourself a link and you can set one in a moment.
                </p>
                <form onSubmit={signInWithPassword}>
                  <input
                    className="signin-second__input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    aria-label="Your password"
                  />
                  {status ? <p className="signin-second__hint">{status}</p> : null}
                  <button className="signin-second__submit" type="submit" disabled={busy || !password}>
                    {busy ? "Signing in…" : "Sign in"}
                  </button>
                </form>
                <button type="button" className="signin-second__link" onClick={sendClaimLink} disabled={busy}>
                  Email me a link to set my password
                </button>
              </>
            )}
            <button type="button" className="signin-second__back" onClick={() => { setStep("identify"); setLinkSent(""); setStatus(""); }}>
              Use a different email
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
